import {
  FluidFieldValidation,
  FluidFlexAlignment,
  FluidFormElementState,
  FluidFormState,
  FluidLanguage,
  FluidOption,
  FluidPopoverConfig,
  FluidRadioGroupConfig,
  FluidRadioSelectorType,
  FluidTextOverflow,
  FluidTheme,
  FluidThemeInterface,
  isNullOrUndefined,
} from '@lmig/fluid-core';
import {
  Component,
  ComponentInterface,
  Element,
  Event,
  EventEmitter,
  h,
  Method,
  Prop,
  State,
  Watch,
} from '@stencil/core';

import {
  FormLabel,
  Validation,
} from '../../common/fluid-common-form.components';
import { INVALID_OPTION_VALUE } from '../../constants/constants';
import { FluidFormElementsInterface } from '../../model/forms/fluid-form-elements.interface';
import {
  setI18nState,
  useI18n,
} from '../../providers/fluid-i18n-provider/adapter/fluid-i18n.adapter';
import { FluidI18nStore } from '../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import {
  getValue,
  handleAutoFocus,
  markTouchedAndValidate,
  reset,
  setValue,
  update,
  validateElement,
} from '../../utils/forms/form-method.utils';
import {
  elementClass,
  setInitialElementState,
} from '../../utils/forms/form-utils';
import { Utils } from '../../utils/utils';
import {
  ButtonRadioGroup,
  DefaultRadioGroup,
  FluidRadioGroupTranslationOptions,
  InlineQuestionStyle,
} from './components/fluid-radio-group.components';

/**
 * @displayName Radio Group
 *
 * @contextData FLUID Radio Group is a Form Element component that allows users to make a single selection from a list of 2+ mutually exclusive options. Unlike Toggle Group (i.e. checkboxes), only ONE option can be selected at a time - clicking a non-selected Radio Button will deselect all other options in the list. Radio Group can be displayed with traditional radio buttons, or as a FLUID Button Group using the "selectorType" Prop.
 * <br/><br/>
 * For the best user experience, a single Radio Group should not have more than 8 options. Consider splitting groups with 6+ options into multiple Radio Groups, or using a FLUID Select component (instead of Radio Group) to display the options within a dropdown menu.
 *
 * @advancedUsage /forms
 */
@Component({
  tag: 'fluid-radio-group',
  styleUrl: 'fluid-radio-group.less',
  shadow: true,
})
export class FluidRadioGroup
  implements
    ComponentInterface,
    FluidThemeInterface,
    FluidFormElementsInterface
{
  // ================================================================ //
  // -- Own Properties (Private)

  /**
   * String that stores the value of initially selected option AFTER
   * validation. Used in "reset" method when "clear" param is false.
   */
  private _validatedInitialValue: string;

  /**
   * Boolean that indicates whether "initializeState()" has been run with a
   * populated "options" array (i.e. options.length > 0). If "options" array
   * is empty/undefined on first render, this Prop will cause "initializeState()"
   * to be run again (instead of "updateState()") when "options" array changes.
   */
  private _stateInitialized = false;

  // ================================================================ //
  // -- Validation Variables

  required: number;

  /**
   * Boolean that tracks whether the form field has received :focus and/or
   * been manipulated since initial page load.
   */
  touched = false;

  /**
   * Reference to the parent FLUID Form state.
   */
  _parentState: FluidFormState;

  /**
   * Boolean that tracks whether the form field has received autofocus
   */
  _autoFocus = false;

  /**
   * Tracks the element ref of the native input component
   */
  _elementRef: HTMLElement;

  // ================================================================ //
  // -- Host Element

  /**
   * Get a reference to the host element
   */
  @Element() host: HTMLFluidRadioGroupElement;

  // ================================================================ //
  // -- States

  /**
   * Derived form field state that tracks value, errors, validity, etc.
   * This common interface is shared among all form fields.
   */
  @State() _elementState: FluidFormElementState;

  // ================================================================ //
  // -- Props

  /**
   * A unique name for the field (usually the dataKey for any form using
   * the input element
   */
  @Prop({ mutable: true }) controlName: string;

  /**
   * What theme settings should the component use?
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * A label to show with the input field (where applicable)
   */
  @Prop({ mutable: true }) label: string;

  /**
   * Sets the Form Label's text overflow behavior. By default, overflowing
   * label text will be truncated with ellipsis.
   */
  @Prop({ mutable: true }) labelTextOverflow: FluidTextOverflow;

  /**
   * Optional placeholder to add to the field
   */
  @Prop({ mutable: true }) placeholder: string;

  /**
   * Should the field be disabled or not
   */
  @Prop({ mutable: true }) disabled: boolean;

  /**
   * If true, the field will be in a "readonly" state.
   */
  @Prop({ mutable: true }) readonly = false;

  /**
   * Value of the option that should be selected by default on first load.
   * NOTE: alternatively, you can also set the default selected option
   * using FluidOption's "selected" property in the "options" array.
   * No options will be selected if this Prop is undefined AND no
   * FluidOptions in the "options" array have "selected: true".
   */
  @Prop({ mutable: true }) initialValue: string;

  /**
   * An array of field validation options
   */
  @Prop({ mutable: true }) validation: FluidFieldValidation[];

  /**
   * A muted label to show after the main form label
   */
  @Prop({ mutable: true }) supplementaryLabel: string;
  /**
   * The list of radio options to render.
   */
  @Prop({ mutable: true }) options: FluidOption[];

  @Watch('options')
  onOptionsChanged(newOpts: FluidOption[], oldOpts: FluidOption[]) {
    switch (true) {
      case Utils.isEqual(newOpts, oldOpts):
        return;
      case !this._stateInitialized && !this._elementState?.value:
        this.initializeState();
        return;
      default:
        this.updateState();
    }
  }

  /**
   * Allows maximum width (in px) to be set for the Radio Group.
   */
  @Prop({ mutable: true }) maxWidthPx: number;

  /**
   * Allows maximum width to be set for the Radio Group. Accepts an
   * integer (e.g. "50" will become 50%).
   */
  @Prop({ mutable: true }) maxWidthPercent: number;

  /**
   * If the field is an inline one, remove all margins so it sits
   * nicely in its container.
   */
  @Prop({ mutable: true }) inlineField: boolean;

  /**
   * If provided, Radio Group's Form Label will render a "helptext" icon
   * with attached Popover. To render a basic Popover with default
   * Prop values, simply provide the Popover's message as a string
   * value to this Prop. To render a Popover with customized Props,
   * provide a FluidPopoverConfig object to this Prop with the "message"
   * property set. For more information, please see Popover's API
   * documentation.
   */
  @Prop({ mutable: true }) helpText: string | FluidPopoverConfig;

  /**
   * When true, Radio Group will have a "questionnaire" layout (i.e.
   * the Form Label will be rendered "inline" to the left of the radio
   * buttons). By default, the radio buttons will be displayed in a
   * horizontal list adjacent to the Form Label - however, this
   * Prop can be combined with the "verticalLayout" Prop to display
   * the radio buttons in a vertical list.
   */
  @Prop({ mutable: true }) inlineQuestion = false;

  /**
   * If group is an inline question, allows you to configure the grid
   * size to use for the question label.
   */
  @Prop({ mutable: true }) questionWidth = 6;

  /**
   * If true, the radio group option will be surrounded by a field border.
   * This should only be used where there are minimal options, and the
   * field is the middle of a grid form (where no borders would look out
   * of place).
   */
  @Prop({ mutable: true }) bordered: boolean;

  /**
   * If true, radio buttons will render vertically rather than horizontally.
   */
  @Prop({ mutable: true }) verticalLayout: boolean;

  /**
   * NOTE: BOTH "inlineQuestion" AND "verticalLayout" Props MUST be true for
   * this Prop to work correctly. Sets the vertical alignment of the Radio
   * Group's label in relation to the list of options (i.e. radio buttons).
   */
  @Prop({ mutable: true }) verticalAlignment: FluidFlexAlignment =
    FluidFlexAlignment.CENTER;

  /**
   * Determines how the Radio Group is rendered:
   * 'default' - Default HTML radio buttons
   * 'button' - FLUID Button Group
   */
  @Prop({ mutable: true }) selectorType: FluidRadioSelectorType =
    FluidRadioSelectorType.DEFAULT;

  /**
   * If true, this field will automatically have focus on page load.
   * Note: only one form field should have this autoFocus set to true
   * on a page. If multiple fields have autoFocus = true, then there
   * could be unexpected behaviour.
   */
  @Prop({ mutable: true }) autoFocus = false;

  /**
   * Allows form element configuration to be passed as a single property.
   */
  @Prop({ mutable: true }) config: FluidRadioGroupConfig;

  @Watch('config')
  onConfigChanged() {
    Utils.selectiveMapConfigToLocal(this, this.config);
  }

  // ================================================================ //
  // -- Events

  /**
   * Emits the element state when its value changes
   */
  @Event() valueChange: EventEmitter<FluidFormElementState>;

  // ================================================================ //
  // -- Component Set-Up

  /**
   * Called every time the component is connected to the DOM.
   * @action: setGlobalTheme - updates theme to match global theme & subscribes to changes.
   */
  connectedCallback(): void {
    Utils.selectiveMapConfigToLocal(this, this.config);
    Utils.setGlobalTheme(this);
    useI18n.bind(this)({
      init: () => setI18nState.bind(this)(),
      language: () => setI18nState.bind(this)(),
    });
  }

  /**
   * Called every time the component is disconnected from the DOM.
   * @action deregisterElement - removes the element from the i18n Store registry
   */
  disconnectedCallback() {
    this._i18nStore?.deregisterElement(this._translateId);
  }

  /**
   * Called once just after the component is first connected to the DOM.
   * @action: initializeState - Sets initial selected option & creates internal state.
   */
  componentWillLoad() {
    this.initializeState();
    this._autoFocus = this.autoFocus;
  }

  /**
   * Called AFTER every render().
   */
  componentDidRender() {
    handleAutoFocus(this);
  }

  // ================================================================ //
  // -- Public Methods

  @Method() async setValue(value: any, opts?: Record<string, any>) {
    return setValue(
      this,
      isNullOrUndefined(value)
        ? value // If value is undefined, pass along to setValue() so reset() is still called
        : this.validateValue(value, false, true),
      null,
      opts
    );
  }

  @Method() async getValue() {
    return getValue(this);
  }

  @Method() async validate() {
    return validateElement(this);
  }

  @Method() async reset(clear = false) {
    this._elementRef = undefined; // _elementRef will be redefined on next render
    return reset(this, clear, () => {
      if (clear) return;
      this._elementState = setInitialElementState(
        this._validatedInitialValue,
        this.controlName
      );
    });
  }

  @Method() async markTouchedAndValidate() {
    return markTouchedAndValidate(this);
  }

  @Method() async setParentValue(formState: FluidFormState) {
    this._parentState = formState;
  }

  // ================================================================ //
  // -- Local Methods

  /**
   * Sets initially selected option & initializes Form state. If
   * "initialValue" Prop is provided, validates that the value exists
   * within the "options" array. If "initialValue" is NOT provided,
   * searches the "options" array for FluidOption with "selected: true".
   */
  initializeState(): void {
    this._stateInitialized = !!this.options?.length;

    if (isNullOrUndefined(this.initialValue)) {
      this._validatedInitialValue = this.getSelectedOptionValue();
    } else {
      this._validatedInitialValue = this.validateValue(
        this.initialValue,
        false,
        this._stateInitialized
      );
    }

    this._elementState = setInitialElementState(
      this._validatedInitialValue,
      this.controlName
    );
  }

  /**
   * Updates Radio Group's internal state when "options" Prop changes.
   * Sets selected option from the new "options" array OR attempts to
   * retain the previously selected option IF it exists in the new
   * "options" array AND its "selected" property is NOT explicitly set
   * to "false".
   */
  updateState(): void {
    if (isNullOrUndefined(this._elementState)) return;

    this._elementState.value =
      this.getSelectedOptionValue() ||
      this.validateValue(this._elementState.value, true, false);
  }

  /**
   * Validates that the provided value exists within "options" array.
   * Optionally, also checks that "selected" property of specified value
   * is NOT explicitly false.
   * @param value - the value to validate
   * @param checkSelected - when true, value will be considered invalid if "selected: false"
   * @param invalidWarning - whether to display a console warning message
   * @return - the validated value OR empty string (if value does NOT pass validation)
   */
  validateValue(
    value: any,
    checkSelected?: boolean,
    invalidWarning?: boolean
  ): string {
    switch (true) {
      case isNullOrUndefined(value) || !this.options?.length:
        return '';
      case !this.options.find(
        (opt) =>
          `${opt.value}` === `${value}` &&
          (checkSelected ? opt.selected !== false : true)
      ):
        invalidWarning &&
          Utils.consoleWarn(this.host, INVALID_OPTION_VALUE(value));
        return '';
      default:
        return `${value ?? ''}`;
    }
  }

  /**
   * Finds & returns the "value" property of the FluidOption with
   * "selected: true" in the "options" array (if one exists). Returns
   * empty string if "options" array doesn't contain a selected option or
   * if the "options" array is undefined.
   */
  getSelectedOptionValue(): string {
    return `${this.options?.find((opt) => opt.selected)?.value ?? ''}`;
  }

  // ================================================================ //
  // -- Render (Main)

  render() {
    const styles = elementClass(
      'fluid-radio-group',
      this.theme,
      this._elementState,
      this.inlineField,
      this.touched
    );

    const formContainer = {
      'form-container': true,
      bordered: this.bordered,
      'inline-question': this.inlineQuestion,
      'button-group': this.selectorType === FluidRadioSelectorType.BUTTON,
    };

    const props = {
      options: this.options,
      label: this.label,
      controlName: this.controlName,
      markTouchedAndValidate: this.markTouchedAndValidate.bind(this),
      update: update,
      host: this,
      verticalLayout: this.verticalLayout,
      selectorType: this.selectorType,
      maxWidthPx: this.maxWidthPx,
      maxWidthPercent: this.maxWidthPercent,
      questionWidth: this.questionWidth || 6,
      setRef: (el) => (this._elementRef = el),
      i18nStore: this._i18nStore,
    };

    return (
      <div class={styles.componentWrapper}>
        <div
          class={{
            ...styles.formGroupClass,
            'inline-question': this.inlineQuestion,
          }}>
          {!this.inlineQuestion && (
            <FormLabel host={this} resetPosition={!this.bordered} />
          )}
          <div class={formContainer}>
            {this.inlineQuestion && <InlineQuestionStyle {...props} />}
            {!this.inlineQuestion && <DefaultRadioGroup {...props} />}
            {!this.inlineQuestion && <ButtonRadioGroup {...props} />}
          </div>
          <div
            class={{
              'form-element-notifications': true,
              'inline-field': this.inlineField,
            }}>
            <Validation host={this} fluidTranslate={this.fluidTranslate} />
          </div>
        </div>
      </div>
    );
  }

  // ================================================================ //
  // -- Internal Props
  //
  // -- Note: This is not a public API - these properties
  // -- should only be used internally by FLUID components.

  /**
   * @internal
   */
  @Prop({ mutable: true }) _standalone = true;

  /**
   * @internal
   */
  @Prop({ mutable: true }) parentState: FluidFormState;

  /**
   * @internal
   */
  @Prop({ mutable: true }) autoValidateChildren: boolean;

  @Watch('parentState') async onParentStateChange(newState: FluidFormState) {
    this._parentState = newState;
    if (this.autoValidateChildren) {
      await this.validate();
    }
  }

  /**
   * @internal
   */
  @Prop({ mutable: true }) externalState: Record<string, any>;

  // ================================================================ //
  // -- Internationalisation

  /* @internal */
  @Prop({ mutable: true }) fluidTranslate: FluidRadioGroupTranslationOptions;
  _translateId: string;
  _i18nStore: FluidI18nStore | undefined;
  @State() _lang: FluidLanguage;
  @State() _i18nBound = false;
}
